import { formatQuantity } from '@/lib/format';

import type { UnitConversion, UnitOfMeasure } from './contracts';
/** A conversion rendered the way an operator reads it: "1 caixa = 30 kg". */
export interface ReadableConversion {
  readonly id: string;
  /** Left-hand side, always one of the alternative unit: "1 caixa". */
  readonly from: string;
  /** Right-hand side already multiplied out: "30 kg". */
  readonly to: string;
  /** The whole sentence, for a screen reader and for the compact list. */
  readonly sentence: string;
  /** The inverse, shown as supporting copy: "1 kg = 0,033 cx". */
  readonly inverseSentence: string;
  /** Sorting key — larger packaging first, which is the order an operator buys in. */
  readonly factor: number;
}

/** Index of the units a conversion refers to, keyed the way the API addresses them. */
export type UnitIndex = ReadonlyMap<string, UnitOfMeasure>;

export function indexUnits(units: readonly UnitOfMeasure[]): UnitIndex {
  return new Map(units.map((unit) => [unit.id, unit]));
}

/**
 * `factor` is a `numeric(18,6)` column, so Pydantic serialises it as a JSON **string** rather
 * than risking a binary float — `UnitConversionResponse.factor` is typed `string` in the
 * generated contract. The write model accepts a number too, so both spellings are coerced here,
 * once, and no screen has to remember which one arrived.
 */
export function toFactor(value: UnitConversion['factor'] | number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * How many `to` one `from` holds when both units fix their own size (migration 0029's
 * `reference_factor`): 1 t → kg is 1000, 1 g → kg is 0,001 — for every product alike, which is
 * why the operator is not asked for it. `null` when either side is counted (a box holds what the
 * product says) or the two measure different things.
 */
export function standardFactor(
  from: UnitOfMeasure | undefined,
  to: UnitOfMeasure | undefined,
): number | null {
  if (from === undefined || to === undefined || from.dimension !== to.dimension) {
    return null;
  }
  const fromSize = from.reference_factor == null ? null : Number(from.reference_factor);
  const toSize = to.reference_factor == null ? null : Number(to.reference_factor);
  if (fromSize === null || toSize === null || !(fromSize > 0) || !(toSize > 0)) {
    return null;
  }
  // numeric(18,6): the precision the API compares the factor at.
  return Math.round((fromSize / toSize) * 1_000_000) / 1_000_000;
}

/** A unit spoken rather than stored: the full word, lower-cased for mid-sentence use. */
function unitName(unit: UnitOfMeasure): string {
  return unit.name.trim() === '' ? unit.symbol : unit.name.toLocaleLowerCase('pt-BR');
}

/**
 * Three decimals is what `formatQuantity` renders and what a purchasing decision needs; keeping
 * more would show a precision the operator cannot act on and invite a false sense of exactness.
 */
function roundForReading(value: number): number {
  return Math.round(value * 1_000) / 1_000;
}

/**
 * Wrong quantities in distribution almost always come from a conversion the operator had to do
 * in their head. So the factor is never shown as a bare number: it is expanded into the sentence
 * the person would otherwise have to derive, in both directions.
 *
 * Returns `null` when either unit is missing from the index — a half-rendered equivalence
 * ("1 ? = 30 kg") is worse than none, because it still looks like an answer.
 */
export function describeConversion(
  conversion: UnitConversion,
  units: UnitIndex,
): ReadableConversion | null {
  const fromUnit = units.get(conversion.from_unit_id);
  const toUnit = units.get(conversion.to_unit_id);
  if (fromUnit === undefined || toUnit === undefined) {
    return null;
  }

  const factor = toFactor(conversion.factor);
  const from = `1 ${unitName(fromUnit)}`;
  const to = `${formatQuantity(factor)} ${toUnit.symbol}`;
  const inverseFactor = factor === 0 ? 0 : 1 / factor;

  return {
    id: conversion.id,
    from,
    to,
    sentence: `${from} = ${to}`,
    inverseSentence: `1 ${unitName(toUnit)} = ${formatQuantity(roundForReading(inverseFactor))} ${fromUnit.symbol}`,
    factor,
  };
}

/** Every conversion that can be read, largest packaging first; unresolvable ones are dropped. */
export function describeConversions(
  conversions: readonly UnitConversion[],
  units: UnitIndex,
): readonly ReadableConversion[] {
  return conversions
    .map((conversion) => describeConversion(conversion, units))
    .filter((readable): readable is ReadableConversion => readable !== null)
    .sort((left, right) => right.factor - left.factor);
}
